import hardCodedValues from "./hardCodedValues";
import { getErrorReporter } from "./telemetry/error-reporter-container";
import get from "lodash/get";

const ErrorReporter = getErrorReporter();

export const topMatSku = function(topMatSku, accentMatSku) {
  if (topMatSku === hardCodedValues.accentMatName) {
    if (accentMatSku === hardCodedValues.offWhiteMatName) {
      return hardCodedValues.offWhiteMatName;
    } else {
      return hardCodedValues.whiteMatName;
    }
  } else {
    return topMatSku || hardCodedValues.whiteMatName;
  }
};

export const getSizeGroups = function() {
  return hardCodedValues.sizeGroups.slice();
};

export const determineSizeGroup = function(side1, side2, method = "uploaded") {
  const side1Rounded = roundNearest(side1, 1/8);
  const side2Rounded = roundNearest(side2, 1/8);

  let output = null;
  const sizeType = "exactsize";

  const exact = hardCodedValues.sizeGroups.filter(function(sizeGroup) {
    return sizeGroup.type === sizeType;
  });

  exact.forEach(function(sizeGroup) {
    const supportedSize = sizeGroup.conveyanceMethods.includes(method);

    if (!output && supportedSize && side1 === sizeGroup.width && side2 === sizeGroup.height) {
      output = sizeGroup;
    }
    if (!output && supportedSize && side2 === sizeGroup.width && side1 === sizeGroup.height) {
      output = sizeGroup;
    }
  });

  if (!output) {
    const notExact = hardCodedValues.sizeGroups.filter(function(sizeGroup) {
      return sizeGroup.type !== sizeType;
    });

    notExact.forEach(function(sizeGroup) {
      const supportedSize = sizeGroup.conveyanceMethods.includes(method);
      
      if (!output && supportedSize && side1Rounded <= sizeGroup.width && side2Rounded <= sizeGroup.height) {
        output = sizeGroup;
      }
      if (!output && supportedSize && side2Rounded <= sizeGroup.width && side1Rounded <= sizeGroup.height) {
        output = sizeGroup;
      }
    });
  }

  return output;
};

export const determineDefaultMatWidth = function(artSide1, artSide2) {
  const sizeGroup = determineSizeGroup(artSide1, artSide2);
  if (sizeGroup && typeof sizeGroup.matWidth === "number") {
    return sizeGroup.matWidth;
  }
  return 0;
};






export const isBase64MatOption = function(mat) {
  const isBase64 = hardCodedValues.base64MatOptions.includes(mat);
  return isBase64;
};

export const isOversized = function(side1, side2) {
  const bigSideMax = Math.max.apply(null, hardCodedValues.maxArtworkDimensions);
  const smallSideMax = Math.min.apply(
    null,
    hardCodedValues.maxArtworkDimensions
  );
  const bigSideMaxString = (bigSideMax + 1) | "+";

  if (side1 === bigSideMaxString || side2 === bigSideMaxString) {
    return true;
  }

  if (side1 > smallSideMax && side2 > smallSideMax) {
    return true;
  }

  return false;
};

const determineMaxSizeForPixels = (dimensionsInPixels) => {
  const {height, width} = dimensionsInPixels;
  const aspectRatio = height / width;
  const maxWidthInInches = Math.floor(width / hardCodedValues.minimumPixelsPerInchForPrinting);
  const maxHeightInInches = Math.floor(maxWidthInInches * aspectRatio * 100) / 100;

  return {
      widthInInches: maxWidthInInches,
      heightInInches: maxHeightInInches
    };
};

const formatHeightInInches = function(height) {
  const formattedHeight = height.toFixed(2);

  if (formattedHeight.substr(-2) === "00") {
    return height.toFixed(0);
  } else if (formattedHeight.substr(-1) === "0") {
    return height.toFixed(1);
  }

  return formattedHeight;
};

export const uploadArtworkSizeOptions = function(
  widthInPixels,
  heightInPixels
) {

  const maxDimsForImage = determineMaxSizeForPixels({width: widthInPixels, height: heightInPixels});

  const ppiRule = (w,h) => {
    return w <= maxDimsForImage.widthInInches && h <= maxDimsForImage.heightInInches;
  };

  const selectOptions = [];
  const ratio = heightInPixels / widthInPixels;
  const max = Math.max.apply(null, hardCodedValues.maxArtworkDimensions);
  let iterator = Math.min.apply(null, hardCodedValues.minArtworkDimensions);

  while (iterator <= max) {
    const width = iterator;
    const height = iterator * ratio;
    const presentation = `${width}" x ${formatHeightInInches(height)}"`;
    const sizeGroup = determineSizeGroup(width, height);

    if (!isOversized(width, height) && ppiRule(width, height)) {
      const option = {
        widthInInches: width,
        heightInInches: height,
        presentation: presentation,
        sizeGroup: sizeGroup
      };
      selectOptions.push(option);
    }
    iterator++;
  }

  return selectOptions;
};


export const getMatSurfaceColor = function(matName) {
  const mat = findMatColor(matName);
  if (mat) {
    return mat.surfaceColor;
  }
};

export const getMatSurfaceTexture = function(matName) {
  const mat = findMatColor(matName);
  if (mat) {
    return mat.surfaceTexture;
  }
};

export const getMatCoreColors = function(matName) {
  const mat = findMatColor(matName);
  if (mat) {
    return mat.coreColors;
  }
};

export const getMatThickness = function(matSku) {
  const mat = findMatColor(matSku);
  if (mat && typeof mat.thickness !== "undefined") {
    return mat.thickness;
  } else {
    ErrorReporter.captureMessage("No mat thickness defined for sku", {
      extra: {
        matSku: matSku
      }
    });
  }
};


export const getMouldingInfo = function(permalink, spree) {
  const promise = spree.mouldings().then(function(response) {
    const mouldingInfo = response.find(function(moulding) {
      if (!moulding || !moulding.permalink) {
        return false;
      } else {
        return moulding.permalink === permalink;
      }
    });

    if (!mouldingInfo) {
      ErrorReporter.captureMessage(
        "Missing moulding information for moulding permalink",
        {
          extra: {
            mouldingPermalink: permalink
          }
        }
      );
    }

    return mouldingInfo;
  });
  return promise;
};

export const getMouldingWidth = function(permalink, spree) {
  const promise = getMouldingInfo(permalink, spree)
    .then(function(moulding) {
      if (moulding && moulding.length_in_inches !== 0) {
        return parseFloat(moulding.length_in_inches);
      } else {
        ErrorReporter.captureMessage(
          "Don't have moulding width for permalink",
          {
            extra: {
              mouldingPermalink: permalink
            }
          }
        );
      }
    });
  return promise;
};

export const lookupMouldingWidthIfNotPresent = function(
  mouldingSpec,
  spree
) {
  let mouldingWidthPromise;
  if (mouldingSpec.mouldingWidth) {
    // if the moulding width was passed to us, let's use it
    mouldingWidthPromise = new Promise(function(resolve) {
      resolve(mouldingSpec.mouldingWidth);
    });
  } else {
    // otherwise look it up
    mouldingWidthPromise = getMouldingWidth(
      mouldingSpec.permalink,
      spree
    );
  }
  return mouldingWidthPromise;
};

export const getMouldingTileInfo = function(permalink, spree) {
  const promise = getMouldingInfo(permalink, spree)
    .then(function(moulding) {
      const usableTile = moulding && moulding.tiles && moulding.tiles[0];
      if (!usableTile || !usableTile.image_url) {
        ErrorReporter.captureMessage(
          "No moulding tile for moulding permalink is unavailable",
          {
            extra: {
              mouldingPermalink: permalink
            }
          }
        );
        return undefined;
      }

      let width;
      let height;
      if (usableTile.image_metadata && usableTile.image_metadata.original) {
        width = usableTile.image_metadata.original.width;
        height = usableTile.image_metadata.original.height;
      }
      return [usableTile.image_url, width, height];
    });
  return promise;
};

export const lookupMouldingTileIfNotPresent = function(
  mouldingSpec,
  spree
) {
  let mouldingTilePromise;
  if (mouldingSpec.url) {
    // if the moulding tile was passed to us, let's use it
    mouldingTilePromise = new Promise(function(resolve) {
      resolve([
        mouldingSpec.url,
        mouldingSpec.widthInPixels,
        mouldingSpec.heightInPixels
      ]);
    });
  } else {
    // otherwise look it up
    mouldingTilePromise = getMouldingTileInfo(
      mouldingSpec.permalink,
      spree
    );
  }
  return mouldingTilePromise;
};

export const normalizedArtworkData = function(artwork) {
  const crop_geometry = artwork.crop_geometry || {};
  return {
    id: artwork.id,
    artworkId: artwork.id,
    imageFileName: artwork.image_file_name,
    token: artwork.token,
    square: {
      url: artwork.square_thumbnail_url
    },
    thumbnail: {
      url: artwork.thumbnail_url
    },
    croppedAndNormalized: {
      url: artwork.cropped_and_normalized_url,
      dimensionsInPixels: {
        width: get(artwork, ["cropped_and_normalized_dimensions", 0]),
        height: get(artwork, ["cropped_and_normalized_dimensions", 1])
      }
    },
    customerSpecified: {
      url: artwork.customer_specified_url,
      dimensionsInPixels: {
        width: get(artwork, ["customer_specified_dimensions", 0]),
        height: get(artwork, ["customer_specified_dimensions", 1])
      }
    },
    normalized: {
      url: artwork.normalized_url,
      dimensionsInPixels: {
        width: get(artwork, ["normalized_dimensions", 0]),
        height: get(artwork, ["normalized_dimensions", 1])
      }
    },
    original: {
      url: artwork.image_url,
      dimensionsInPixels: {
        width: get(artwork, ["image_dimensions", "0"]),
        height: get(artwork, ["image_dimensions", "1"])
      }
    },
    originalUnedited: {
      url: artwork.original_unedited_url,
      dimensionsInPixels: {
        width: get(artwork, ["original_unedited_dimensions", "0"]),
        height: get(artwork, ["original_unedited_dimensions", "1"])
      }
    },
    imageContentType: artwork.image_content_type,
    rotate: artwork.rotate,
    // crop_geometry is to determine if a crop has been applied server side
    crop_geometry: crop_geometry,
    mount: {}
  };
};



/**
 * Determines the maximum size for thisSide based on thatSide, and a limit
 * described by an array of two numbers, such as Framebridge size groups. For
 * example, if our upper limit for artwork is 32 x 40, we represent this as
 * [32, 40]. If this side is 30 & that side is 35, the maximum for this side
 * will be 32 because we can't have 2 sides that go over 32.
 * @function
 * @param {SizeGroupLimit} limits - The spot to put the new item in.
 * @param {number} thisSide - The current size of this side.
 * @param {number} thatSide - The current size of the other size.
 * @returns {number}
 */
export const maxSize = function(limits, thisSide, thatSide) {
  const smallSideLimit = Math.min(...limits);
  const largeSideLimit = Math.max(...limits);
  if (thatSide <= smallSideLimit) {
    return largeSideLimit;
  } else {
    // if that side is bigger than the larger limit, then this side must stay
    // under the smaller limit
    return smallSideLimit;
  }
};

/**
 * Determines the minimum size for thisSide based on thatSide, and a limit
 * described by an array of two numbers, such as Framebridge size groups. For
 * example, if our lower limit for artwork is 4 x 6, we represent this as
 * [4, 6]. If this side is 7 & that side is 5, the minimum for this side will be
 * 6 because we can't have 2 sides that go under 6.
 * @function
 * @param {SizeGroupLimit} limits - The spot to put the new item in.
 * @param {number} thisSide - The current size of this side.
 * @param {number} thatSide - The current size of the other size.
 * @returns {number}
 */
export const minSize = function(limits, thisSide, thatSide) {
  const smallSideLimit = Math.min(...limits);
  const largeSideLimit = Math.max(...limits);
  if (thatSide >= largeSideLimit) {
    return smallSideLimit;
  } else {
    // if that side is bigger than the larger limit, then this side must stay
    // under the smaller limit
    return largeSideLimit;
  }
};

export const roundDown = function(value, increment) {
  return Math.floor(value / increment) * increment;
};

export const roundNearest = function(value, increment) {
  return Math.round(value / increment) * increment;
};


export const closeTo = function(num1, num2, delta) {
  if (typeof delta === "undefined") {
    delta = 0.00001;
  }
  return Math.abs(num1 - num2) <= delta;
};

export const doAspectRatiosMatch = function(
  dimensionsA,
  dimensionsB,
  bidirectionalCheck
) {
  let dimensionA1 = dimensionsA.pop();
  let dimensionA2 = dimensionsA.pop();
  let dimensionB1 = dimensionsB.pop();
  let dimensionB2 = dimensionsB.pop();

  if (!dimensionA1 || !dimensionA2 || !dimensionB1 || !dimensionB2) {
    return false;
  }

  dimensionA1 = Number(dimensionA1);
  dimensionA2 = Number(dimensionA2);
  dimensionB1 = Number(dimensionB1);
  dimensionB2 = Number(dimensionB2);

  const dimensionARatio1 = dimensionA1 / dimensionA2;
  const dimensionARatio2 = dimensionA2 / dimensionA1;
  const dimensionBRatio1 = dimensionB1 / dimensionB2;

  const ratio1Matches = closeTo(
    dimensionARatio1,
    dimensionBRatio1
  );
  const ratio2Matches = closeTo(
    dimensionARatio2,
    dimensionBRatio1
  );

  if (bidirectionalCheck) {
    return ratio1Matches || ratio2Matches;
  } else {
    return ratio1Matches;
  }
};

const minimumArtworkSizeOption = function (dimensionsInPixels) {
  const widthInInches = Math.min.apply(null, hardCodedValues.minArtworkDimensions);
  const heightInInches = widthInInches * (dimensionsInPixels.height / dimensionsInPixels.width);

  return {
    widthInInches: widthInInches,
    heightInInches: heightInInches,
    presentation: `${widthInInches}" x ${formatHeightInInches(heightInInches)}"`,
    sizeGroup: determineSizeGroup(widthInInches, heightInInches)
  };
};

export const determineMinSizeForSizeGroup = function (dimensionsInPixels, args) {
  const limitSizeGroup = getSizeGroups().find((group) => {
    return group.name == args.sizeGroup;
  });
  const sizeOptions = uploadArtworkSizeOptions(...Object.values(dimensionsInPixels));
  const validDimensionOptions = sizeOptions.filter((sizeOption) => {
    return sizeOption.sizeGroup && sizeOption.sizeGroup.sequence <= limitSizeGroup.sequence;
  });

  // Attempt to find the first of the limiting size group, otherwise falling back to the last option
  const firstLimitingSizeGroup = validDimensionOptions.find((sizeOption) => {
    return sizeOption.sizeGroup.name == limitSizeGroup.name;
  });
  const lastOption = validDimensionOptions[validDimensionOptions.length - 1];

  // Portraits taller than 3:1 overshoot the limiting size group at every width, and portraits
  // under 4in wide at printable resolution produce no options at all. Both used to return
  // undefined, leaving callers with undefined artwork dimensions.
  return firstLimitingSizeGroup
    || lastOption
    || sizeOptions[0]
    || minimumArtworkSizeOption(dimensionsInPixels);
};

export const determineMaxSizeForSizeGroup = function (dimensionsInPixels, args) {
  const limitSizeGroup = getSizeGroups().find((group) => {
    return group.name == args.sizeGroup;
  });

  const validDimensionOptions = uploadArtworkSizeOptions(...Object.values(dimensionsInPixels))
    .filter((sizeOption) => {
      return sizeOption.sizeGroup && sizeOption.sizeGroup.sequence <= limitSizeGroup.sequence;
    });

  const lastLimitingSizeGroup = [...validDimensionOptions].reverse().find((sizeOption) => {
    return sizeOption.sizeGroup.name == limitSizeGroup.name;
  });

  const lastOption = validDimensionOptions[validDimensionOptions.length - 1];

  return lastLimitingSizeGroup || lastOption;
};

export const determineSizeDefaults = function (dimensionsInPixels, args) {
  let sizeDefault = "max";
  if (args) {
    sizeDefault = args.sizeDefault || "max";
  }
  if (sizeDefault === "max") {
    return determineMinSizeForSizeGroup(dimensionsInPixels, {sizeDefault: "minForSizeGroup", sizeGroup: "GR"});
  } else if (sizeDefault === "minForSizeGroup") {
    return determineMinSizeForSizeGroup(dimensionsInPixels, args);
  }
};

export default {
  topMatSku,
  getSizeGroups,
  determineSizeGroup,
  determineDefaultMatWidth,
  isBase64MatOption,
  isOversized,
  uploadArtworkSizeOptions,
  getMatSurfaceColor,
  getMatSurfaceTexture,
  getMatCoreColors,
  getMatThickness,
  getMouldingInfo,
  getMouldingWidth,
  lookupMouldingWidthIfNotPresent,
  getMouldingTileInfo,
  lookupMouldingTileIfNotPresent,
  normalizedArtworkData,
  maxSize,
  minSize,
  roundDown,
  roundNearest,
  closeTo,
  doAspectRatiosMatch,
  determineMinSizeForSizeGroup,
  determineMaxSizeForSizeGroup,
  determineSizeDefaults
};
